import {DnsRecord} from './types';

// FNP-1192 — THE VALUE COLUMN. This file is the point of the ticket.
//
// ⚠️ THE FAILURE THIS EXISTS TO PREVENT.
//
// A record that POINTS AT ANOTHER RESOURCE — a Route 53 alias, a Cloudflare
// flattened CNAME — has no literal rdata of its own. At the provider, the
// target sits in a separate provider-specific structure and the rdata field is
// EMPTY. A naive value cell reads the rdata, gets nothing, and renders blank —
// so the operator cannot tell an alias from a broken record, which are the two
// most different things a row can be.
//
// The adapter already solved half of this: it resolves the pointer's target
// INTO `values` and sets `isPointer` (FNP-1184). So the rule for this module is
// short and worth stating plainly:
//
//   RENDER `values`. IT IS ALREADY THE TARGET.
//
// There is no separate `target` field on the schema and nothing here may look
// for one. What this file adds is the two things `values` alone cannot say:
// that a row is a pointer (so the operator knows why it has no TTL and cannot
// be edited as rdata), and what to show when `values` really is empty.

// ⚠️ AN EMPTY `values` IS NOT THE POINTER CASE, and conflating the two is how
// the original bug comes back wearing a fix.
//
// A pointer has its target IN `values` — the adapter put it there. So an empty
// `values` means something else entirely: the provider returned a record with
// no rdata and no pointer target. That is rare and it is not normal, and the
// honest cell says so rather than rendering an empty string that looks like a
// styling bug.
export const EMPTY_VALUE_TEXT = 'No value reported';

/**
 * The text for a record's value cell.
 *
 * Multi-valued records join with a newline rather than a comma: an MX or TXT
 * record routinely holds several values, and a comma is a legal character
 * INSIDE a TXT value, so comma-joining produces a string that cannot be taken
 * apart again by eye.
 */
export const recordValueText = (record: DnsRecord): string => {

    const values = (record.values ?? []).filter(
        value => typeof value === 'string' && value !== '',
    );

    if (values.length === 0) {
        return EMPTY_VALUE_TEXT;
    }

    return values.join('\n');
};

/**
 * Whether the cell is showing a real answer or reporting the absence of one.
 *
 * The table renders the two differently — a real value is body text, an absent
 * one is dimmed — so that "no value reported" cannot be mistaken for a value
 * that happens to read like a sentence.
 */
export const hasReportedValue = (record: DnsRecord): boolean =>
    (record.values ?? []).some(value => typeof value === 'string' && value !== '');

/**
 * The TTL cell.
 *
 * ⚠️ A POINTER HAS NO TTL OF ITS OWN and the schema returns null for it — the
 * provider resolves the pointer and answers with the TARGET's TTL, which is not
 * a property of this record. Rendering `0` there would put a number on screen
 * that no provider reported and that an operator would reasonably read as a
 * real, and alarmingly short, TTL.
 *
 * So null renders as an em dash for a pointer (with the pointer badge next to
 * it carrying the explanation) and as an em dash for any other record that
 * simply did not report one. Both are "not a number we have", which is the
 * truth in both cases.
 */
export const ttlText = (record: DnsRecord): string => {
    if (typeof record.ttlSeconds !== 'number') {
        return '—';
    }
    return String(record.ttlSeconds);
};
